import type { TimeSlotMenu } from '../types/timeSlots';

export interface MenuFilterResult {
  /** The menus to reason about. Never empty when `menus` was non-empty. */
  menus: TimeSlotMenu[];
  /**
   * True when a filter was set but matched none of the candidates — the guest's
   * committed menu is not offered here. Callers MUST force the EXPERIENCE step
   * on this, so the guest chooses from what is actually available instead of
   * being handed a substitute they never picked.
   */
  filterMismatched: boolean;
}

/**
 * Restrict a slot's menus to the guest's active menu filter.
 *
 * `booking.selectedMenuFilter` records a menu the guest has already committed
 * to — set at the MENU_FILTER step, from a `preSelectedMenuId` landing, or by
 * the marketplace redirect. Every site that decides whether the EXPERIENCE
 * step is needed must apply it, or the sites disagree on `menuCount` and the
 * guest is asked to re-pick a menu they already chose. This mirrors the rule
 * `shouldShowExperience` already encodes for `preSelectedMenuId`.
 *
 * Apply this AFTER narrowing by area. Area is a hard constraint; the menu
 * filter is a preference. Filtering by menu first can narrow to zero when the
 * guest's menu is not served in the chosen area, and a zero-length set skips
 * both the EXPERIENCE step and the single-menu auto-assign — committing a
 * booking with no menu at all.
 *
 * A mismatch is reported rather than silently resolved. Returning the
 * unfiltered set alone would auto-assign a menu the guest did not choose,
 * which is indistinguishable from a legitimate single-menu assignment; pairing
 * it with `filterMismatched` lets the caller show the choice instead.
 *
 * Pure; never mutates its input.
 */
export function applySelectedMenuFilter(
  menus: TimeSlotMenu[],
  selectedMenuFilter: number[] | null | undefined,
): MenuFilterResult {
  if (!selectedMenuFilter || selectedMenuFilter.length === 0) {
    return { menus, filterMismatched: false };
  }

  const filterSet = new Set(selectedMenuFilter);
  const filtered = menus.filter((m) => filterSet.has(m.id));

  if (filtered.length > 0) return { menus: filtered, filterMismatched: false };

  // Nothing matched. Hand back what IS on offer, flagged, so the caller can
  // put the decision in front of the guest. An empty candidate list is not a
  // mismatch — there is simply nothing here to choose from.
  return { menus, filterMismatched: menus.length > 0 };
}
